import type { BannerState, BannerLayer, DyeColorId, BannerPatternId, BannerEnchantment } from './types';
import { createLayer } from './types';
import {
  DYE_COLORS,
  DYE_COLOR_MAP,
  BANNER_PATTERNS,
  BANNER_PATTERN_MAP,
  getAvailablePatterns,
  patternRequiresItem,
} from './constants';
import { unicodeBtoa, unicodeAtob } from '@/app/lib/base64';
import { renderTextComponent, renderTextComponentList, renderEnchantmentsMap, usesBareSnbtText } from '@/app/lib/minecraft/textComponent';

// ── Helpers ────────────────────────────────────────────────────────────

function buildNameComponent(name: string, noItalic: boolean, version: string): string {
  if (!name) return '';
  const obj: Record<string, unknown> = { text: name };
  if (noItalic) obj.italic = false;
  return `custom_name=${renderTextComponent(obj, version)}`;
}

function buildLoreComponent(lore: string[], noItalic: boolean, version: string): string {
  const lines = lore.filter(l => l.length > 0);
  if (lines.length === 0) return '';
  const objs = lines.map(line => {
    const obj: Record<string, unknown> = { text: line };
    if (noItalic) obj.italic = false;
    return obj;
  });
  return `lore=${renderTextComponentList(objs, version)}`;
}

function buildEnchantmentsComponent(enchantments: BannerEnchantment[], version: string): string {
  if (enchantments.length === 0) return '';
  // The `levels:{...}` wrapper is REQUIRED for 1.20.5–1.21.4 and was only
  // removed starting 1.21.5 — renderEnchantmentsMap's flat form is only
  // valid from 1.21.5 onward, so branch here rather than calling it unconditionally.
  if (usesBareSnbtText(version)) {
    return `enchantments=${renderEnchantmentsMap(enchantments, version)}`;
  }
  const entries = enchantments.map(e => `"${e.id}":${e.level}`);
  return `enchantments={levels:{${entries.join(',')}}}`;
}

// Legacy (pre-1.20.5) display NBT. Routes through the shared helper like the
// component path above — raw JSON.stringify in a single-quoted SNBT string did NOT
// escape the wrapping quote, so a name like "Bob's Banner" closed the string early
// and broke the /give. Quote-free output stays byte-identical to the old form.
function buildNameNbt(name: string, noItalic: boolean, version: string): string {
  if (!name) return '';
  const obj: Record<string, unknown> = { text: name };
  if (noItalic) obj.italic = false;
  return `Name:${renderTextComponent(obj, version)}`;
}

function buildLoreNbt(lore: string[], noItalic: boolean, version: string): string {
  const lines = lore.filter(l => l.length > 0);
  if (lines.length === 0) return '';
  const objs = lines.map(line => {
    const obj: Record<string, unknown> = { text: line };
    if (noItalic) obj.italic = false;
    return obj;
  });
  return `Lore:${renderTextComponentList(objs, version)}`;
}

function buildEnchantmentsNbt(enchantments: BannerEnchantment[]): string {
  if (enchantments.length === 0) return '';
  const entries = enchantments.map(e => `{id:"${e.id}",lvl:${e.level}s}`);
  return `Enchantments:[${entries.join(',')}]`;
}

// ── Banner Command Generation ──────────────────────────────────────────

/**
 * The state as this version can express it: layers whose pattern the version lacks are left out.
 * `flow` and `guster` are 1.21+; a layer kept from a newer version (a version switch, a share link)
 * made the whole 1.20.5–1.20.6 /give fail ("Failed to get element") and wrote an unknown legacy code
 * before that. The editor flags them (patternsUnavailableIn).
 */
export function forVersion(state: BannerState, version: string): BannerState {
  const ok = new Set(getAvailablePatterns(version).map(p => p.id));
  return state.layers.every(l => ok.has(l.pattern)) ? state : { ...state, layers: state.layers.filter(l => ok.has(l.pattern)) };
}

/** Enabled layers this version cannot draw — for the editor's notice. */
export function patternsUnavailableIn(state: BannerState, version: string): string[] {
  const ok = new Set(getAvailablePatterns(version).map(p => p.id));
  return [...new Set(state.layers.filter(l => l.enabled && !ok.has(l.pattern)).map(l => l.pattern))];
}

export function generateModernCommand(state: BannerState, target: string, version: string): string {
  const baseItem = `minecraft:${state.baseColor}_banner`;
  const enabledLayers = state.layers.filter(l => l.enabled);

  const components: string[] = [];

  if (enabledLayers.length > 0) {
    const patterns = enabledLayers.map(layer =>
      `{pattern:"${layer.pattern}",color:"${layer.color}"}`
    );
    components.push(`minecraft:banner_patterns=[${patterns.join(',')}]`);
  }

  if (state.enchantments.length > 0) components.push(buildEnchantmentsComponent(state.enchantments, version));
  if (state.customName) components.push(buildNameComponent(state.customName, state.noItalic, version));
  const loreComp = buildLoreComponent(state.lore, state.noItalic, version);
  if (loreComp) components.push(loreComp);
  if (state.glintOverride !== null) components.push(`enchantment_glint_override=${state.glintOverride}`);

  if (components.length === 0) {
    return `/give ${target} ${baseItem} 1`;
  }

  return `/give ${target} ${baseItem}[${components.join(',')}] 1`;
}

export function generateLegacyCommand(state: BannerState, target: string, version: string): string {
  const baseItem = `minecraft:${state.baseColor}_banner`;
  const enabledLayers = state.layers.filter(l => l.enabled);

  const nbtParts: string[] = [];

  if (enabledLayers.length > 0) {
    const patterns = enabledLayers.map(layer => {
      const patternInfo = BANNER_PATTERN_MAP.get(layer.pattern)!;
      const colorInfo = DYE_COLOR_MAP.get(layer.color)!;
      return `{Pattern:"${patternInfo.legacyCode}",Color:${colorInfo.legacyId}}`;
    });
    nbtParts.push(`BlockEntityTag:{Patterns:[${patterns.join(',')}]}`);
  }

  if (state.enchantments.length > 0) nbtParts.push(buildEnchantmentsNbt(state.enchantments));

  const displayParts: string[] = [];
  const nameNbt = buildNameNbt(state.customName, state.noItalic, version);
  if (nameNbt) displayParts.push(nameNbt);
  const loreNbt = buildLoreNbt(state.lore, state.noItalic, version);
  if (loreNbt) displayParts.push(loreNbt);
  if (displayParts.length > 0) nbtParts.push(`display:{${displayParts.join(',')}}`);

  if (nbtParts.length === 0) {
    return `/give ${target} ${baseItem} 1`;
  }

  return `/give ${target} ${baseItem}{${nbtParts.join(',')}} 1`;
}

export function generateCommand(state: BannerState, useModernFormat: boolean, version: string, target: string = '@p'): string {
  state = forVersion(state, version);
  return useModernFormat
    ? generateModernCommand(state, target, version)
    : generateLegacyCommand(state, target, version);
}

// ── Shield Command Generation ─────────────────────────────────────────

/**
 * 🚨 **A shield's BASE COLOR is meaningful on its own, and dropping it loses the whole design.**
 *
 * All four shield builders used to emit the base color only *inside* `if (enabledLayers.length)`,
 * so a shield with a chosen color and no patterns — **the default state of the tool** — produced a
 * plain wooden shield while the preview showed a solid colored one. `renderer.ts` fills the shield
 * face with the base color unconditionally, before any layer, so preview and command disagreed for
 * every user who picked a color and stopped there.
 *
 * A solid-colored shield is a real vanilla item, not a curiosity: 26.2's `ShieldDecorationRecipe`
 * reads the banner's patterns with `getOrDefault(BANNER_PATTERNS, EMPTY)` and then sets **both**
 * `BANNER_PATTERNS` and `BASE_COLOR` on the result, so crafting with a plain undyed-pattern banner
 * gives exactly this. Confirmed kept by real servers on all three eras (legacy
 * `BlockEntityTag:{Base:14}`, and `minecraft:base_color` on 1.21.4 and 26.2).
 *
 * Both helpers exist so the color cannot go missing from one of four near-identical builders
 * again — which is how it went missing from all four.
 */
function shieldModernComponents(state: BannerState): string[] {
  const enabledLayers = state.layers.filter(l => l.enabled);
  const components = [`minecraft:base_color="${state.baseColor}"`];
  if (enabledLayers.length > 0) {
    const patterns = enabledLayers.map(layer =>
      `{pattern:"${layer.pattern}",color:"${layer.color}"}`
    );
    components.push(`minecraft:banner_patterns=[${patterns.join(',')}]`);
  }
  return components;
}

/** The same rule for the pre-1.20.5 shape: `Base` always, `Patterns` only when there are some. */
function shieldLegacyBlockEntityTag(state: BannerState): string {
  const enabledLayers = state.layers.filter(l => l.enabled);
  const baseColorInfo = DYE_COLOR_MAP.get(state.baseColor)!;
  if (enabledLayers.length === 0) {
    return `BlockEntityTag:{Base:${baseColorInfo.legacyId}}`;
  }
  const patterns = enabledLayers.map(layer => {
    const patternInfo = BANNER_PATTERN_MAP.get(layer.pattern)!;
    const colorInfo = DYE_COLOR_MAP.get(layer.color)!;
    return `{Pattern:"${patternInfo.legacyCode}",Color:${colorInfo.legacyId}}`;
  });
  return `BlockEntityTag:{Base:${baseColorInfo.legacyId},Patterns:[${patterns.join(',')}]}`;
}

export function generateShieldModernCommand(state: BannerState, target: string, version: string): string {
  const components: string[] = [...shieldModernComponents(state)];

  if (state.enchantments.length > 0) components.push(buildEnchantmentsComponent(state.enchantments, version));
  if (state.customName) components.push(buildNameComponent(state.customName, state.noItalic, version));
  const loreComp = buildLoreComponent(state.lore, state.noItalic, version);
  if (loreComp) components.push(loreComp);
  if (state.glintOverride !== null) components.push(`enchantment_glint_override=${state.glintOverride}`);

  if (components.length === 0) {
    return `/give ${target} minecraft:shield 1`;
  }

  return `/give ${target} minecraft:shield[${components.join(',')}] 1`;
}

export function generateShieldLegacyCommand(state: BannerState, target: string, version: string): string {

  const nbtParts: string[] = [shieldLegacyBlockEntityTag(state)];

  if (state.enchantments.length > 0) nbtParts.push(buildEnchantmentsNbt(state.enchantments));

  const displayParts: string[] = [];
  const nameNbt = buildNameNbt(state.customName, state.noItalic, version);
  if (nameNbt) displayParts.push(nameNbt);
  const loreNbt = buildLoreNbt(state.lore, state.noItalic, version);
  if (loreNbt) displayParts.push(loreNbt);
  if (displayParts.length > 0) nbtParts.push(`display:{${displayParts.join(',')}}`);

  if (nbtParts.length === 0) {
    return `/give ${target} minecraft:shield 1`;
  }

  return `/give ${target} minecraft:shield{${nbtParts.join(',')}} 1`;
}

export function generateShieldCommand(state: BannerState, useModernFormat: boolean, version: string, target: string = '@p'): string {
  state = forVersion(state, version);
  return useModernFormat
    ? generateShieldModernCommand(state, target, version)
    : generateShieldLegacyCommand(state, target, version);
}

// ── Wear on Head Command ─────────────────────────────────────────────

export function generateWearOnHeadModern(state: BannerState, target: string, version: string): string {
  const baseItem = `minecraft:${state.baseColor}_banner`;
  const enabledLayers = state.layers.filter(l => l.enabled);

  const components: string[] = [];

  if (enabledLayers.length > 0) {
    const patterns = enabledLayers.map(layer =>
      `{pattern:"${layer.pattern}",color:"${layer.color}"}`
    );
    components.push(`minecraft:banner_patterns=[${patterns.join(',')}]`);
  }

  // Same item as /give: enchantments and the glint override were left off the worn banner.
  if (state.enchantments.length > 0) components.push(buildEnchantmentsComponent(state.enchantments, version));
  if (state.customName) components.push(buildNameComponent(state.customName, state.noItalic, version));
  const loreComp = buildLoreComponent(state.lore, state.noItalic, version);
  if (loreComp) components.push(loreComp);
  if (state.glintOverride !== null) components.push(`enchantment_glint_override=${state.glintOverride}`);

  const itemWithComponents = components.length > 0
    ? `${baseItem}[${components.join(',')}]`
    : baseItem;

  return `/item replace entity ${target} armor.head with ${itemWithComponents}`;
}

export function generateWearOnHeadLegacy(state: BannerState, target: string, useItemCommand: boolean, version: string): string {
  const baseItem = `minecraft:${state.baseColor}_banner`;
  const enabledLayers = state.layers.filter(l => l.enabled);

  const nbtParts: string[] = [];

  if (enabledLayers.length > 0) {
    const patterns = enabledLayers.map(layer => {
      const patternInfo = BANNER_PATTERN_MAP.get(layer.pattern)!;
      const colorInfo = DYE_COLOR_MAP.get(layer.color)!;
      return `{Pattern:"${patternInfo.legacyCode}",Color:${colorInfo.legacyId}}`;
    });
    nbtParts.push(`BlockEntityTag:{Patterns:[${patterns.join(',')}]}`);
  }

  if (state.enchantments.length > 0) nbtParts.push(buildEnchantmentsNbt(state.enchantments));

  const displayParts: string[] = [];
  const nameNbt = buildNameNbt(state.customName, state.noItalic, version);
  if (nameNbt) displayParts.push(nameNbt);
  const loreNbt = buildLoreNbt(state.lore, state.noItalic, version);
  if (loreNbt) displayParts.push(loreNbt);
  if (displayParts.length > 0) nbtParts.push(`display:{${displayParts.join(',')}}`);

  const nbtStr = nbtParts.length > 0 ? `{${nbtParts.join(',')}}` : '';

  if (useItemCommand) {
    return `/item replace entity ${target} armor.head with ${baseItem}${nbtStr}`;
  }
  return `/replaceitem entity ${target} slot.armor.head ${baseItem}${nbtStr}`;
}

export function generateWearOnHeadCommand(state: BannerState, useModernFormat: boolean, useItemCommand: boolean, version: string, target: string = '@p'): string {
  state = forVersion(state, version);
  if (useModernFormat) {
    return generateWearOnHeadModern(state, target, version);
  }
  return generateWearOnHeadLegacy(state, target, useItemCommand, version);
}

// ── Equip Shield to Offhand ─────────────────────────────────────────────

function generateShieldEquipModern(state: BannerState, target: string, version: string): string {
  const components: string[] = [...shieldModernComponents(state)];

  if (state.enchantments.length > 0) components.push(buildEnchantmentsComponent(state.enchantments, version));
  if (state.customName) components.push(buildNameComponent(state.customName, state.noItalic, version));
  const loreComp = buildLoreComponent(state.lore, state.noItalic, version);
  if (loreComp) components.push(loreComp);
  if (state.glintOverride !== null) components.push(`enchantment_glint_override=${state.glintOverride}`);

  const itemWithComponents = components.length > 0
    ? `minecraft:shield[${components.join(',')}]`
    : 'minecraft:shield';

  return `/item replace entity ${target} weapon.offhand with ${itemWithComponents}`;
}

function generateShieldEquipLegacy(state: BannerState, target: string, useItemCommand: boolean, version: string): string {
  const nbtParts: string[] = [shieldLegacyBlockEntityTag(state)];

  if (state.enchantments.length > 0) nbtParts.push(buildEnchantmentsNbt(state.enchantments));

  const displayParts: string[] = [];
  const nameNbt = buildNameNbt(state.customName, state.noItalic, version);
  if (nameNbt) displayParts.push(nameNbt);
  const loreNbt = buildLoreNbt(state.lore, state.noItalic, version);
  if (loreNbt) displayParts.push(loreNbt);
  if (displayParts.length > 0) nbtParts.push(`display:{${displayParts.join(',')}}`);

  const nbtStr = nbtParts.length > 0 ? `{${nbtParts.join(',')}}` : '';

  if (useItemCommand) {
    return `/item replace entity ${target} weapon.offhand with minecraft:shield${nbtStr}`;
  }
  return `/replaceitem entity ${target} slot.weapon.offhand minecraft:shield${nbtStr}`;
}

export function generateShieldEquipCommand(state: BannerState, useModernFormat: boolean, useItemCommand: boolean, version: string, target: string = '@p'): string {
  state = forVersion(state, version);
  if (useModernFormat) {
    return generateShieldEquipModern(state, target, version);
  }
  return generateShieldEquipLegacy(state, target, useItemCommand, version);
}

// ── Crafting Steps ──────────────────────────────────────────────────────

export interface CraftingStep {
  stepNumber: number;
  patternName: string;
  patternId: BannerPatternId;
  dyeColorName: string;
  dyeColorHex: string;
  dyeColorId: DyeColorId;
  requiresPatternItem: boolean;
  patternItemName?: string;
}

const PATTERN_ITEM_NAMES: Partial<Record<string, string>> = {
  'minecraft:creeper': 'Creeper Charge',
  'minecraft:skull': 'Skull Charge',
  'minecraft:flower': 'Flower Charge',
  'minecraft:mojang': 'Thing',
  'minecraft:globe': 'Globe',
  'minecraft:piglin': 'Snout',
  'minecraft:flow': 'Flow',
  'minecraft:guster': 'Guster',
  'minecraft:curly_border': 'Bordure Indented',
  'minecraft:bricks': 'Field Masoned',
};

export function generateCraftingSteps(state: BannerState, version: string): CraftingStep[] {
  return state.layers.filter(l => l.enabled).map((layer, idx) => {
    const pattern = BANNER_PATTERN_MAP.get(layer.pattern)!;
    const color = DYE_COLOR_MAP.get(layer.color)!;
    return {
      stepNumber: idx + 1,
      patternName: pattern.displayName,
      patternId: layer.pattern,
      dyeColorName: color.displayName,
      dyeColorHex: color.hex,
      dyeColorId: layer.color,
      requiresPatternItem: patternRequiresItem(pattern, version),
      patternItemName: patternRequiresItem(pattern, version) ? PATTERN_ITEM_NAMES[layer.pattern] : undefined,
    };
  });
}

// ── Materials Summary ───────────────────────────────────────────────────

export interface MaterialsSummary {
  dyes: Map<DyeColorId, { name: string; hex: string; count: number }>;
  patternItems: string[];
}

export function getMaterialsSummary(state: BannerState, version: string): MaterialsSummary {
  const dyes = new Map<DyeColorId, { name: string; hex: string; count: number }>();
  const patternItems: string[] = [];

  for (const layer of state.layers.filter(l => l.enabled)) {
    const color = DYE_COLOR_MAP.get(layer.color)!;
    const existing = dyes.get(layer.color);
    if (existing) {
      existing.count++;
    } else {
      dyes.set(layer.color, { name: color.displayName, hex: color.hex, count: 1 });
    }

    const pattern = BANNER_PATTERN_MAP.get(layer.pattern)!;
    if (patternRequiresItem(pattern, version)) {
      const itemName = PATTERN_ITEM_NAMES[layer.pattern] || pattern.displayName;
      patternItems.push(itemName + ' Banner Pattern');
    }
  }

  return { dyes, patternItems };
}

// ── JSON Export/Import ──────────────────────────────────────────────────

export interface BannerExportData {
  version: 1 | 2;
  baseColor: DyeColorId;
  layers: Array<{ pattern: BannerPatternId; color: DyeColorId; enabled?: boolean }>;
  // v2 fields
  enchantments?: BannerEnchantment[];
  customName?: string;
  lore?: string[];
  noItalic?: boolean;
  glintOverride?: boolean | null;
}

export function exportToJSON(state: BannerState): string {
  const data: BannerExportData = {
    version: 2,
    baseColor: state.baseColor,
    layers: state.layers.map(l => ({ pattern: l.pattern, color: l.color, enabled: l.enabled })),
    enchantments: state.enchantments,
    customName: state.customName,
    lore: state.lore,
    noItalic: state.noItalic,
    glintOverride: state.glintOverride,
  };
  return JSON.stringify(data, null, 2);
}

export function importFromJSON(json: string): Partial<BannerState> | null {
  try {
    const data = JSON.parse(json) as BannerExportData;
    if (data.version !== 1 && data.version !== 2) return null;

    // Validate base color
    if (!DYE_COLOR_MAP.has(data.baseColor)) return null;

    // Validate and create layers
    const layers: BannerLayer[] = [];
    if (!Array.isArray(data.layers)) return null;
    for (const l of data.layers) {
      if (!l || !BANNER_PATTERN_MAP.has(l.pattern)) return null;
      if (!DYE_COLOR_MAP.has(l.color)) return null;
      layers.push(createLayer(l.pattern, l.color, l.enabled !== false));
    }

    const result: Partial<BannerState> = { baseColor: data.baseColor, layers };

    // v2 fields — the same field-by-field checks as the share-link extras (decodeExtras). The import
    // used to take `enchantments` whole, so an id with a `"` or `}` reached the command; that is the
    // injection decodeExtras was hardened against, left open on this path.
    if (data.version === 2) {
      if (Array.isArray(data.enchantments)) {
        result.enchantments = data.enchantments.slice(0, 64).map(toBannerEnchantment).filter((x): x is BannerEnchantment => x !== null);
      }
      if (typeof data.customName === 'string') result.customName = data.customName.slice(0, 256);
      if (Array.isArray(data.lore)) result.lore = data.lore.filter((x): x is string => typeof x === 'string').slice(0, 64).map(x => x.slice(0, 5120));
      if (typeof data.noItalic === 'boolean') result.noItalic = data.noItalic;
      if (typeof data.glintOverride === 'boolean' || data.glintOverride === null) result.glintOverride = data.glintOverride;
    }

    return result;
  } catch {
    return null;
  }
}

// ── URL Hash Encoding ───────────────────────────────────────────────────

// Format: baseColorIdx:patternIdx-colorIdx,patternIdx-colorIdx,...[:extras]
// Base color index is hex (0-f), pattern index is base36 (0-42), color index is hex (0-f).
//
// The optional trailing `:extras` segment is a base64url-encoded compact JSON
// blob carrying enchantments/customName/lore/noItalic/glintOverride — fields
// that don't fit the original positional format. It's ONLY appended when at
// least one of those fields differs from its default, so every hash produced
// before this segment existed (and every hash with nothing but base+layers)
// round-trips byte-for-byte identically. Old links have no third `:` segment,
// so `hash.split(':')` simply yields `undefined` for it and decode falls back
// to defaults — no format migration needed.

export interface BannerHashExtras {
  e?: BannerEnchantment[]; // enchantments
  n?: string;              // customName
  l?: string[];            // lore
  i?: boolean;             // noItalic — only present when it's false (default is true)
  g?: boolean | null;      // glintOverride — only present when non-null
}

const EXTRAS_DEFAULTS = {
  enchantments: [] as BannerEnchantment[],
  customName: '',
  lore: [] as string[],
  noItalic: true,
  glintOverride: null as boolean | null,
};

/** base64 → URL-safe base64 (no padding), so it drops cleanly into a `:`-delimited hash segment. */
function toBase64Url(str: string): string {
  return unicodeBtoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(str: string): string {
  let b64 = str.replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4;
  if (pad === 2) b64 += '==';
  else if (pad === 3) b64 += '=';
  else if (pad === 1) throw new Error('invalid base64url length');
  return unicodeAtob(b64);
}

// A resource location (`minecraft:unbreaking`, `mymod:thing`) — the only shape an enchantment id can take.
// 🚨 Load-bearing: the id is written into the command VERBATIM (`"${e.id}":${e.level}`), so an id carrying
// `"`, `}` or `,` closed the map and appended arbitrary item components — a hidden custom_name/lore in a
// design whose preview and review queue show only patterns. Anything else in a hash is dropped.
const ENCHANTMENT_ID_RE = /^(?:[a-z0-9_.-]+:)?[a-z0-9_./-]+$/;

/** Rebuilds each entry from its two fields only, so extra properties never survive a decode. */
function toBannerEnchantment(e: unknown): BannerEnchantment | null {
  if (!e || typeof e !== 'object') return null;
  const { id, level } = e as { id?: unknown; level?: unknown };
  if (typeof id !== 'string' || id.length > 128 || !ENCHANTMENT_ID_RE.test(id)) return null;
  // The editor allows 1–255 ("bypass limits"); anything else is not something it could have produced.
  if (typeof level !== 'number' || !Number.isInteger(level) || level < 1 || level > 255) return null;
  return { id, level };
}

function encodeExtras(state: BannerState): string | null {
  const extras: BannerHashExtras = {};
  if (state.enchantments.length > 0) extras.e = state.enchantments;
  if (state.customName) extras.n = state.customName;
  if (state.lore.some(l => l.length > 0)) extras.l = state.lore;
  if (state.noItalic === false) extras.i = false;
  if (state.glintOverride !== null) extras.g = state.glintOverride;

  if (Object.keys(extras).length === 0) return null;

  try {
    return toBase64Url(JSON.stringify(extras));
  } catch {
    return null;
  }
}

function decodeExtras(encoded: string | undefined): typeof EXTRAS_DEFAULTS {
  if (!encoded) return { ...EXTRAS_DEFAULTS };
  try {
    const parsed = JSON.parse(fromBase64Url(encoded)) as BannerHashExtras;
    return {
      enchantments: Array.isArray(parsed.e)
        ? parsed.e.slice(0, 64).map(toBannerEnchantment).filter((x): x is BannerEnchantment => x !== null)
        : EXTRAS_DEFAULTS.enchantments,
      // Same caps as importFromJSON (the hash had none).
      customName: typeof parsed.n === 'string' ? parsed.n.slice(0, 256) : EXTRAS_DEFAULTS.customName,
      lore: Array.isArray(parsed.l) ? parsed.l.filter((x): x is string => typeof x === 'string').slice(0, 64).map(x => x.slice(0, 5120)) : EXTRAS_DEFAULTS.lore,
      noItalic: parsed.i === false ? false : EXTRAS_DEFAULTS.noItalic,
      glintOverride: parsed.g === true ? true : parsed.g === false ? false : EXTRAS_DEFAULTS.glintOverride,
    };
  } catch {
    // Malformed/truncated/foreign trailing segment — fall back to defaults,
    // the base design (color + layers) is still returned by the caller.
    return { ...EXTRAS_DEFAULTS };
  }
}

export function encodeToHash(state: BannerState): string {
  const baseIdx = DYE_COLORS.findIndex(c => c.id === state.baseColor);
  const baseHex = baseIdx.toString(16);

  const layerParts = state.layers.map(l => {
    const patIdx = BANNER_PATTERNS.findIndex(p => p.id === l.pattern);
    const colIdx = DYE_COLORS.findIndex(c => c.id === l.color);
    return `${patIdx.toString(36)}-${colIdx.toString(16)}${l.enabled ? '' : '!'}`;
  }).join(',');

  const extras = encodeExtras(state);

  if (extras === null) {
    // Identical to the pre-existing format when there's nothing extra to carry.
    return state.layers.length === 0 ? baseHex : `${baseHex}:${layerParts}`;
  }

  return `${baseHex}:${layerParts}:${extras}`;
}

export interface BannerHashDecodeResult {
  baseColor: DyeColorId;
  layers: BannerLayer[];
  enchantments: BannerEnchantment[];
  customName: string;
  lore: string[];
  noItalic: boolean;
  glintOverride: boolean | null;
}

export function decodeFromHash(hash: string): BannerHashDecodeResult | null {
  try {
    if (!hash || hash.length === 0) return null;

    const [basePart, layersPart, extrasPart] = hash.split(':');
    const baseIdx = parseInt(basePart, 16);
    if (isNaN(baseIdx) || baseIdx < 0 || baseIdx >= DYE_COLORS.length) return null;

    const baseColor = DYE_COLORS[baseIdx].id;
    const extras = decodeExtras(extrasPart);

    if (!layersPart) {
      return { baseColor, layers: [], ...extras };
    }

    const layers: BannerLayer[] = [];
    const parts = layersPart.split(',');
    for (const part of parts) {
      const disabled = part.endsWith('!');
      const cleanPart = disabled ? part.slice(0, -1) : part;
      const [patStr, colStr] = cleanPart.split('-');
      const patIdx = parseInt(patStr, 36);
      const colIdx = parseInt(colStr, 16);
      if (isNaN(patIdx) || isNaN(colIdx)) return null;
      if (patIdx < 0 || patIdx >= BANNER_PATTERNS.length) return null;
      if (colIdx < 0 || colIdx >= DYE_COLORS.length) return null;
      layers.push(createLayer(BANNER_PATTERNS[patIdx].id, DYE_COLORS[colIdx].id, !disabled));
    }

    return { baseColor, layers, ...extras };
  } catch {
    return null;
  }
}
